import moment from "moment";

const {
    toNumber: toCustoNumber,
    ratearDespesa: ratearDespesaCusto,
    applyHeaderRateios,
    computeCustoAtual,
} = require("./custoReal");

const ACQUISITION_PURPOSE_OPTIONS = [
    { value: "REVENDA", label: "Revenda" },
    { value: "INDUSTRIALIZACAO", label: "Industrialização" },
    { value: "USO_CONSUMO", label: "Uso e consumo" },
    { value: "ATIVO_IMOBILIZADO", label: "Ativo imobilizado" },
    { value: "OUTRA", label: "Outra" },
];

/**
 * Precedência visual do item: persistido > default do tenant > vazio.
 * Não persiste. Abrir/editar o select não grava.
 */
function resolveItemPurpose(persisted, tenantDefault, options) {
    const allowed = (options || []).map((opt) => opt.value);
    if (typeof persisted === "string" && allowed.includes(persisted)) {
        return persisted;
    }
    if (typeof tenantDefault === "string" && allowed.includes(tenantDefault)) {
        return tenantDefault;
    }
    return "";
}

export default {
    name: "NFeIntegration",

    props: {
        id: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            integrando: false,
            nfe: null,
            dets: [],
            duplicatas: [],
            freteTipos: [],
            cfopAlerts: [],
            contasDebito: [],
            contasCredito: [],
            error: null,
            success: null,
            data_movimento: moment().format("YYYY-MM-DDTHH:mm"),
            integrarOptions: {
                financeiro: true,
                estoque: true,
            },
            freteTipo: "0",
            vFrete: 0,
            ctes: [],
            acquisitionPurposeEnabled: false,
            acquisitionPurposeDefault: null,
            acquisitionPurposeOptions: ACQUISITION_PURPOSE_OPTIONS,
            modal: {
                loading: false,
                key: null,
                tipo: "novo",
                produtoSelecionado: null,
                produtoName: "",
                qCom: 0,
                vUnCom: 0,
                vProd: 0,
                estoque_minimo: 0,
                active: true,
                observacao: "",
                codigo: "",
                codigos: [],
                newCode: "",
                showFornecedorSearch: false,
                fornecedorSearch: "",
                fornecedorResults: [],
                fornecedorSelected: null,
                unidadesHtml: "",
                categoriasHtml: "",
                marcasHtml: "",
                modelosHtml: "",
                error: null,
            },
        };
    },

    computed: {
        /** Duplicatas válidas (sem null/undefined), para evitar erro quando o array vem com [null]. */
        duplicatasValidas() {
            const arr = this.duplicatas || [];
            return Array.isArray(arr) ? arr.filter((d) => d != null) : [];
        },
        totalCtes() {
            if (!this.ctes || !this.ctes.length) {
                return 0;
            }
            return this.ctes.reduce((sum, cte) => {
                const val = this.toNumber(cte.CTe_infCte_vPrest_vTPrest);
                return sum + val;
            }, 0);
        },
    },

    mounted() {
        this.loadData();
        this.$nextTick(() => this.attachPanelModalFocusFix());
    },

    beforeDestroy() {
        const el = document.getElementById("panelModal");
        if (el && this._panelModalHideListener) {
            el.removeEventListener(
                "hide.bs.modal",
                this._panelModalHideListener
            );
            this._panelModalHideListener = null;
        }
    },

    methods: {
        /**
         * Bootstrap marca aria-hidden no modal ao fechar enquanto o foco ainda
         * está no botão (✓ / ✕), o que gera aviso no console e pode interferir
         * no próximo clique. Removemos o foco antes do hide.
         */
        attachPanelModalFocusFix() {
            const el = document.getElementById("panelModal");
            if (!el || this._panelModalHideListener) {
                return;
            }
            this._panelModalHideListener = () => {
                const a = document.activeElement;
                if (
                    a &&
                    el.contains(a) &&
                    typeof a.blur === "function"
                ) {
                    a.blur();
                }
            };
            el.addEventListener("hide.bs.modal", this._panelModalHideListener);
        },

        // --- Carga e dados iniciais ---
        async loadData() {
            this.loading = true;
            this.error = null;

            try {
                const url = `/compras/nfe/${this.id}/integration/data`;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error("Erro ao buscar a NF-e");
                }
                const payload = await response.json();
                if (payload.error) {
                    throw new Error(payload.message || "Erro ao buscar a NF-e");
                }

                this.nfe = payload.nfe;
                this.duplicatas = payload.duplicatas || [];
                this.freteTipos = payload.freteTipos || [];
                this.cfopAlerts = payload.cfopAlerts || [];
                this.contasDebito = payload.contasDebito || [];
                this.contasCredito = payload.contasCredito || [];
                this.ctes = this.nfe.ctes || [];
                this.acquisitionPurposeEnabled = !!payload.acquisitionPurposeEnabled;
                this.acquisitionPurposeDefault = this.acquisitionPurposeEnabled
                    ? payload.acquisitionPurposeDefault || null
                    : null;
                if (
                    Array.isArray(payload.acquisitionPurposeOptions) &&
                    payload.acquisitionPurposeOptions.length
                ) {
                    this.acquisitionPurposeOptions = payload.acquisitionPurposeOptions;
                }

                if (this.nfe.data_movimento) {
                    const parsed = moment(this.nfe.data_movimento);
                    if (parsed.isValid()) {
                        this.data_movimento = parsed.format("YYYY-MM-DDTHH:mm");
                    }
                }

                this.dets = (payload.dets || []).map((det) => {
                    const data = this.normalizeDet(det.json || {});
                    const original = det.original || {};
                    return { data, original };
                });

                this.initFrete();
                this.recalculaDets();
            } catch (err) {
                console.error(err);
                this.error = err.message || "Falha ao carregar dados";
            } finally {
                this.loading = false;
            }
        },

        initFrete() {
            if (!this.nfe || !this.nfe.xmlStd) {
                return;
            }
            const totals = this.nfe.xmlStd.NFe.infNFe.total.ICMSTot || {};
            const xmlFrete = this.toNumber(totals.vFrete || 0);
            const modFrete = String(
                this.nfe.xmlStd.NFe.infNFe.transp.modFrete || "0"
            );

            if (xmlFrete > 0) {
                this.freteTipo = "1";
                this.vFrete = xmlFrete;
                return;
            }

            this.freteTipo = modFrete;
            this.vFrete = this.totalCtes || 0;
        },

        handleFreteTipo() {
            this.recalculaDets(true);
        },

        rowClass(det) {
            return det.integrado ? "" : "table-warning";
        },

        /**
         * O modelo `Produto` na NF-e costuma vir com `unidade_with_childs`
         * (relação eager em NFeEntradaDet), não com `unidade`.
         */
        resolveProdutoUnidade(produto) {
            if (!produto) {
                return null;
            }
            return (
                produto.unidade ||
                produto.unidade_with_childs ||
                null
            );
        },

        getProdutoUnidadeId(produto) {
            if (!produto) {
                return null;
            }
            const rawId = produto.unidade_id;
            if (rawId != null && rawId !== "" && Number(rawId) > 0) {
                return Number(rawId);
            }
            const u = this.resolveProdutoUnidade(produto);
            if (u && u.id != null && u.id !== "" && Number(u.id) > 0) {
                return Number(u.id);
            }
            return null;
        },

        produtoTemUnidadeCadastro(produto) {
            return this.getProdutoUnidadeId(produto) != null;
        },

        renderUnidade(det) {
            const uCad = this.resolveProdutoUnidade(det.produto);
            if (!det.produto || !uCad) {
                return `<span class="badge bg-primary">${
                    det.uCom || "-"
                }</span>`;
            }

            if (det.uComConverter && !det.uComConvertida) {
                return `<span class="badge bg-danger" title="Unidade precisa de conversão. Cadastro: ${uCad.sigla}"><i class="fas fa-exclamation-triangle text-warning me-1"></i>${det.uCom} -> ${uCad.sigla}</span>`;
            }

            return `<span class="badge bg-primary">${uCad.sigla}</span>`;
        },

        // --- Normalização e helpers numéricos ---
        normalizeDet(det) {
            const normalized = { ...det };
            normalized.key = Number(det.key);
            normalized.integrado = !!det.integrado;
            normalized.uComConverter = !!det.uComConverter;
            normalized.uComConvertida = !!det.uComConvertida;
            normalized.qCom = this.toNumber(det.qCom);
            normalized.vUnCom = this.toNumber(det.vUnCom);
            normalized.vProd = this.toNumber(det.vProd);
            normalized.vFrete = this.toNumber(det.vFrete || 0);
            normalized.vSeg = this.toNumber(det.vSeg || 0);
            normalized.vOutro = this.toNumber(det.vOutro || 0);
            normalized.vDesc = this.toNumber(det.vDesc || 0);
            normalized.vIPI = this.toNumber(det.vIPI || 0);
            normalized.vICMSST = this.toNumber(det.vICMSST || 0);
            normalized.oCusto = Number(det.oCusto || 1);
            normalized.oMargem = Number(det.oMargem || 1);
            normalized.vCustoAnterior = this.toNumber(
                det.vCustoAnterior || 0
            ).toFixed(3);
            normalized.vCustoAtual = det.vCustoAtual
                ? this.toNumber(det.vCustoAtual)
                : 0;
            normalized.vCustoAtualChanged = Number(det.vCustoAtualChanged || 0);
            normalized.changedCusto = !!det.changedCusto;
            normalized.vCustoVendas = this.toNumber(det.vCustoVendas || 0);
            normalized.vCustoVendasChanged = Number(
                det.vCustoVendasChanged || 0
            );
            normalized.vMargemAnterior = this.toNumber(
                det.vMargemAnterior || 0
            ).toFixed(3);
            normalized.vMargemAtual = this.toNumber(
                det.vMargemAtual || 0
            ).toFixed(3);
            normalized.vCustoMedio = this.toNumber(det.vCustoMedio || 0);
            const persistedPurpose = det.acquisition_purpose || "";
            normalized._persistedPurpose = persistedPurpose;
            normalized._persistedNote = det.acquisition_purpose_note || "";
            normalized.acquisition_purpose_note = normalized._persistedNote;
            normalized.acquisition_purpose = this.acquisitionPurposeEnabled
                ? resolveItemPurpose(
                      persistedPurpose,
                      this.acquisitionPurposeDefault,
                      this.acquisitionPurposeOptions
                  )
                : persistedPurpose;
            return normalized;
        },

        toNumber(val) {
            return toCustoNumber(val);
        },

        ratearDespesa(vProd, vDespesa, vItem) {
            return ratearDespesaCusto(vProd, vDespesa, vItem);
        },

        setMaiorCusto(det) {
            if (det.produto && det.changedCusto === false) {
                if (
                    this.toNumber(det.vCustoAtual) >
                    this.toNumber(det.vCustoAnterior)
                ) {
                    det.oCusto = 1;
                } else {
                    det.oCusto = 2;
                }
                det.changedCusto = true;
            }
        },

        calculeCustoMedio(det) {
            let vCustoMedio = 0;
            if (
                det.produto &&
                det.produto.estoque &&
                det.produto.estoque.saldo
            ) {
                const qEstq = this.toNumber(det.produto.estoque.saldo);
                const cEstq = this.toNumber(det.vCustoAnterior);
                const vEstq = cEstq * qEstq;
                const vCustoAtualTotal =
                    this.toNumber(det.vCustoAtual) * this.toNumber(det.qCom);
                const qTot = qEstq + this.toNumber(det.qCom);
                const vTot = vEstq + vCustoAtualTotal;
                vCustoMedio = (vTot / qTot).toFixed(2);
            }
            return vCustoMedio;
        },

        // --- Cálculos por item (det): custo, margem, custo vendas ---
        recalculaDet(det) {
            det.vCustoAtual = computeCustoAtual(det);
            det.vCustoAnterior = this.toNumber(det.vCustoAnterior).toFixed(3);
            det.vMargemAtual = this.toNumber(det.vMargemAtual).toFixed(3);
            det.vMargemAnterior = this.toNumber(det.vMargemAnterior).toFixed(3);

            det.vCustoMedio = this.calculeCustoMedio(det);

            if (!det.oCustoChanged) {
                det.oCusto =
                    this.toNumber(det.vCustoAtual) >
                    this.toNumber(det.vCustoAnterior)
                        ? 1
                        : 2;
            }

            if (!det.vCustoVendasChanged) {
                const custoVendasProduto =
                    det.produto != null && det.produto.custo_vendas != null
                        ? this.toNumber(det.produto.custo_vendas)
                        : 0;
                const vCustoAtualNum = this.toNumber(det.vCustoAtual);
                det.vCustoVendas = Math.max(
                    custoVendasProduto,
                    vCustoAtualNum
                ).toFixed(3);
            }

            const oCustoSelect = document.getElementById(`oCusto-${det.key}`);
            if (oCustoSelect) {
                oCustoSelect.value = det.oCusto;
            }
            const custoInput = document.getElementById(`vCusto-${det.key}`);
            if (custoInput) {
                custoInput.value = this.getVCustoValue(det);
            }
            const custoVendasInput = document.getElementById(
                `vCustoVendas-${det.key}`
            );
            if (custoVendasInput) {
                custoVendasInput.value = det.vCustoVendas;
            }
        },

        recalculaDets(forceMaiorCusto = false) {
            if (!this.nfe || !this.nfe.xmlStd) {
                return;
            }

            const totals = this.nfe.xmlStd.NFe.infNFe.total.ICMSTot || {};
            const vProd = this.toNumber(totals.vProd);
            const vSeg = this.toNumber(totals.vSeg);
            const vOutro = this.toNumber(totals.vOutro);
            const vFrete =
                this.freteTipo === "1" ? this.toNumber(this.vFrete) : 0;

            this.dets.forEach((detWrapper) => {
                const det = detWrapper.data;
                applyHeaderRateios(det, vProd, { vFrete, vSeg, vOutro });

                if (forceMaiorCusto) {
                    det.changedCusto = false;
                }

                this.recalculaDet(det);
            });
        },

        getVCustoValue(det) {
            const o = det.oCusto;
            if (o === 1) return det.vCustoAtual;
            if (o === 2) return det.vCustoAnterior;
            if (o === 3) return det.vCustoMedio;
            return det.vCustoAtual;
        },

        isCustoVendasInvalid(det) {
            const touched =
                det.vCustoVendasChanged ||
                det.vCustoAtualChanged ||
                det.oCustoChanged;
            if (!touched) {
                return false;
            }
            /* custo_vendas >= custo real definido acima (anterior, atual ou médio conforme oCusto) */
            const custoReal = this.toNumber(this.getVCustoValue(det));
            return this.toNumber(det.vCustoVendas) < custoReal;
        },

        getVMargemValue(det) {
            if (det.oMargem === 1) {
                return det.vMargemAtual;
            }
            return det.vMargemAnterior;
        },

        handleOCusto(det) {
            det.oCustoChanged = 1;
            this.recalculaDet(det);
        },

        onChangeVCusto(det, event) {
            const raw = event.target.value;
            const value = this.toNumber(raw);
            if (det.oCusto === 1) {
                det.vCustoAtual = value.toFixed(3);
                det.vProd =
                    this.toNumber(det.vCustoAtual) * this.toNumber(det.qCom);
            } else if (det.oCusto === 2) {
                det.vCustoAnterior = value.toFixed(3);
            } else if (det.oCusto === 3) {
                det.vCustoMedio = value.toFixed(3);
            }

            det.vCustoAtualChanged = 1;
            det.vCustoVendasChanged = 0;
            this.recalculaDet(det);
            if (event && event.target) {
                event.target.value = this.getVCustoValue(det);
            }
        },

        onChangeVCustoVendas(det, event) {
            const raw = event.target.value;
            if (raw === "") {
                det.vCustoVendas = "";
            } else {
                det.vCustoVendas = this.toNumber(raw).toFixed(3);
            }
            det.vCustoVendasChanged = 1;
            if (event && event.target) {
                event.target.value = det.vCustoVendas;
            }
        },

        handleOMargem(det) {
            if (this.toNumber(det.vMargemAnterior) === 0) {
                det.oMargem = 1;
            }
        },

        onChangeVMargem(det, event) {
            const value = this.toNumber(event.target.value);
            if (det.oMargem === 1) {
                det.vMargemAtual = value.toFixed(3);
            } else {
                det.vMargemAnterior = value.toFixed(3);
            }
        },

        onChangeVICMSST(det, event) {
            const raw = event.target.value;
            if (raw === "") {
                det.vICMSST = "";
            } else {
                det.vICMSST = this.toNumber(raw).toFixed(3);
            }
            this.recalculaDet(det);
        },

        // --- Modal assistente: cadastro/vínculo de produto ---
        async openModal(det) {
            this.modal.loading = true;
            this.modal.error = null;
            this.modal.key = det.key;
            this.modal.tipo = det.produto ? "existente" : "novo";
            this.modal.produtoSelecionado = det.produto || null;
            this.modal.produtoName = det.produto ? det.produto.name : det.xProd;
            this.modal.qCom = this.toNumber(det.qCom);
            this.modal.vProd = this.toNumber(det.vProd);
            this.modal.vUnCom = this.toNumber(det.vUnCom);
            this.modal.estoque_minimo = det.produto
                ? this.toNumber(det.produto.estoque_minimo)
                : 0;
            this.modal.active = det.produto ? det.produto.active == 1 : true;
            this.modal.observacao = det.produto ? det.produto.observacao : "";
            this.modal.newCode = "";
            this.modal.showFornecedorSearch = false;
            this.modal.fornecedorSearch = "";
            this.modal.fornecedorResults = [];
            this.modal.fornecedorSelected = null;

            await this.loadUnidades(
                det.uCom,
                det.produto ? det.produto.id : null
            );
            await this.loadCategorias(
                det.produto ? det.produto.categorias : null
            );
            await this.loadMarcas(
                det.produto ? det.produto.marca_id : null,
                det.produto ? det.produto.modelo_id : null
            );
            if (det.produto) {
                await this.loadProdutoCodes(det.produto.id);
                this.ensureCodigoNotaFirst(det);
            } else {
                this.modal.codigos = this.buildCodigoNotaFirst(det);
            }

            this.modal.loading = false;
            this.$nextTick(() =>
                this.setUnidadeSelectValue(
                    det.produto
                        ? this.getProdutoUnidadeId(det.produto)
                        : null
                )
            );
        },

        getEmitenteForCodigo() {
            if (!this.nfe) return { emitenteId: null, empresa: null };
            const emitenteId =
                this.nfe.empresa_id != null ? this.nfe.empresa_id : null;
            const empresa = this.nfe.empresa
                ? { id: this.nfe.empresa.id, name: this.nfe.empresa.name }
                : null;
            return { emitenteId, empresa };
        },

        buildCodigoNotaFirst(det) {
            const codigoNota = det.cProd ? String(det.cProd).trim() : "";
            if (!codigoNota) return [];
            const { emitenteId, empresa } = this.getEmitenteForCodigo();
            return [
                {
                    id: null,
                    code: codigoNota,
                    empresa_id: emitenteId,
                    isOriginal: null,
                    empresa,
                },
            ];
        },

        ensureCodigoNotaFirst(det) {
            const codigoNota = det.cProd ? String(det.cProd).trim() : "";
            if (!codigoNota) return;
            const { emitenteId, empresa } = this.getEmitenteForCodigo();
            const list = this.modal.codigos || [];
            const idx = list.findIndex(
                (c) =>
                    String(c.code).trim() === codigoNota &&
                    (c.empresa_id == null ? null : Number(c.empresa_id)) ===
                        (emitenteId == null ? null : Number(emitenteId))
            );
            if (idx >= 0) {
                const [item] = list.splice(idx, 1);
                this.modal.codigos = [item, ...list];
            } else {
                this.modal.codigos = [
                    {
                        id: null,
                        code: codigoNota,
                        empresa_id: emitenteId,
                        isOriginal: null,
                        empresa,
                    },
                    ...list,
                ];
            }
        },

        setTipoProduto(tipo) {
            this.modal.tipo = tipo;
            if (tipo === "novo") {
                const detWrapper = this.dets.find(
                    (d) => d.data.key === this.modal.key
                );
                const det = detWrapper ? detWrapper.data : null;
                this.modal.codigos = det ? this.buildCodigoNotaFirst(det) : [];
                this.modal.newCode = "";
                this.modal.showFornecedorSearch = false;
                this.modal.fornecedorSearch = "";
                this.modal.fornecedorResults = [];
                this.modal.fornecedorSelected = null;
            }
            this.$nextTick(() => {
                if (tipo === "novo") {
                    const el = this.$refs.produtoNameInput;
                    if (el) (el.$el || el).focus();
                } else if (tipo === "existente") {
                    this.openProductSearch();
                }
            });
        },

        // --- ProductSearch (abrir/fechar e callback) ---
        getProductSearchModalEl() {
            return (
                document.getElementById("searchProdutoModal") ||
                (this.$refs.productSearch?.$el?.querySelector?.(
                    "#searchProdutoModal"
                ) ??
                    null)
            );
        },

        setProductSearchModalVisible(show) {
            const el = this.getProductSearchModalEl();
            if (!el) return;
            if (
                typeof window.$ !== "undefined" &&
                window.$.fn &&
                window.$.fn.modal
            ) {
                window.$(el).modal(show ? "show" : "hide");
            } else if (window.bootstrap && window.bootstrap.Modal) {
                const m = show
                    ? window.bootstrap.Modal.getOrCreateInstance(el)
                    : window.bootstrap.Modal.getInstance(el);
                if (m) m[show ? "show" : "hide"]();
            }
            if (show) {
                setTimeout(() => {
                    const backdrops =
                        document.querySelectorAll(".modal-backdrop");
                    if (backdrops.length)
                        backdrops[backdrops.length - 1].style.zIndex = "1095";
                    el.style.zIndex = "1100";
                }, 50);
            }
        },

        openProductSearch() {
            this.$nextTick(() => this.setProductSearchModalVisible(true));
        },

        productSearchCallback(selected) {
            this.applyProdutoToModal(selected).then(() =>
                this.setProductSearchModalVisible(false)
            );
        },

        async applyProdutoToModal(produtoFromSearch) {
            const detWrapper = this.dets.find(
                (d) => d.data.key === this.modal.key
            );
            const det = detWrapper ? detWrapper.data : null;
            const response = await fetch(
                `/ajax/get-produto?q=${produtoFromSearch.id}`
            );
            const produto = await response.json();
            this.modal.produtoSelecionado = produto;
            this.modal.produtoName = produto.name;
            this.modal.observacao = produto.observacao || "";
            this.modal.estoque_minimo = this.toNumber(
                produto.estoque_minimo || 0
            );
            this.modal.active = produto.active == 1;
            const uCom = det ? det.uCom : null;
            const uSigla =
                produto.unidade?.sigla ||
                this.resolveProdutoUnidade(produto)?.sigla ||
                "";
            await this.loadUnidades(uCom || uSigla, produto.id);
            await this.loadCategorias(produto.categorias || null);
            await this.loadMarcas(
                produto.marca_id || null,
                produto.modelo_id || null
            );
            await this.loadProdutoCodes(produto.id);
            if (det) this.ensureCodigoNotaFirst(det);
            const unidadeId = this.getProdutoUnidadeId(produto);
            this.$nextTick(() => this.setUnidadeSelectValue(unidadeId));
        },

        clearProdutoSelecionado() {
            this.modal.produtoSelecionado = null;
            this.modal.produtoName = "";
            this.modal.codigos = [];
            this.clearAddCodeForm();
        },

        // --- Códigos do produto no modal ---
        async loadProdutoCodes(produto_id) {
            const params = new URLSearchParams({ produto_id });
            if (this.nfe && this.nfe.empresa_id) {
                params.append("empresa_id", this.nfe.empresa_id);
            }
            const response = await fetch(
                `/ajax/get-produto-codes?${params.toString()}`
            );
            this.modal.codigos = await response.json();
        },

        showAddFornecedor() {
            this.modal.showFornecedorSearch = true;
            this.modal.fornecedorSearch = "";
            this.modal.fornecedorResults = [];
            this.modal.fornecedorSelected = null;
            this.$nextTick(() => {
                this.$refs.fornecedorSearchInput?.focus();
            });
        },

        clearAddCodeForm() {
            this.modal.showFornecedorSearch = false;
            this.modal.fornecedorSearch = "";
            this.modal.fornecedorResults = [];
            this.modal.fornecedorSelected = null;
            this.modal.newCode = "";
        },

        async searchFornecedores() {
            const term = this.modal.fornecedorSearch.trim();
            if (term.length < 2) {
                this.modal.fornecedorResults = [];
                return;
            }
            const response = await fetch(
                `/ajax/get-fornecedores?q=${encodeURIComponent(term)}`
            );
            this.modal.fornecedorResults = await response.json();
        },

        selectFornecedor(fornecedor) {
            this.modal.fornecedorSelected = {
                id: fornecedor.id,
                name: fornecedor.name,
            };
            this.modal.fornecedorSearch = fornecedor.name;
            this.modal.fornecedorResults = [];
        },

        addCodigoOriginal() {
            const code = this.modal.newCode.trim();
            if (!code) return;
            this.modal.codigos.push({
                id: null,
                code,
                empresa_id: null,
                isOriginal: 1,
                empresa: null,
            });
            this.modal.newCode = "";
        },

        addCodigoFornecedor() {
            const code = this.modal.newCode.trim();
            if (!code || !this.modal.fornecedorSelected) return;
            this.modal.codigos.push({
                id: null,
                code,
                empresa_id: this.modal.fornecedorSelected.id,
                isOriginal: null,
                empresa: {
                    id: this.modal.fornecedorSelected.id,
                    name: this.modal.fornecedorSelected.name,
                },
            });
            this.modal.newCode = "";
            this.clearAddCodeForm();
        },

        removeCodigo(index) {
            this.modal.codigos.splice(index, 1);
        },

        updateModalQuantidade() {
            const qCom = this.toNumber(this.modal.qCom) || 1;
            const vProd = this.toNumber(this.modal.vProd);
            this.modal.vUnCom = (vProd / qCom).toFixed(4);
        },

        async loadUnidades(uCom, produto_id = null) {
            const params = new URLSearchParams({
                uCom,
            });
            if (produto_id) {
                params.append("produto_id", produto_id);
            }
            const response = await fetch(
                `/ajax/get-unidades-integracao?${params.toString()}`
            );
            this.modal.unidadesHtml = await response.text();
        },

        async loadCategorias(json) {
            const params = new URLSearchParams();
            if (json) {
                params.append("json", JSON.stringify(json));
            }
            const response = await fetch(
                `/ajax/get-categories-html?${params.toString()}`
            );
            this.modal.categoriasHtml = await response.text();
        },

        async loadMarcas(marca_id = null, modelo_id = null) {
            const params = new URLSearchParams();
            if (marca_id) {
                params.append("marca_id", marca_id);
            }
            const response = await fetch(
                `/ajax/get-marcas-html?${params.toString()}`
            );
            this.modal.marcasHtml = await response.text();

            if (marca_id) {
                await this.loadModelos(marca_id, modelo_id);
            } else {
                this.modal.modelosHtml = "";
            }

            this.$nextTick(() => {
                const select = document.getElementById("marca_id");
                if (select) {
                    select.onchange = async (event) => {
                        const selected = event.target.value;
                        if (selected) {
                            await this.loadModelos(selected, null);
                        } else {
                            this.modal.modelosHtml = "";
                        }
                    };
                }
            });
        },

        async loadModelos(marca_id, modelo_id = null) {
            const params = new URLSearchParams({ marca_id });
            if (modelo_id) {
                params.append("modelo_id", modelo_id);
            }
            const response = await fetch(
                `/ajax/get-modelos-html?${params.toString()}`
            );
            this.modal.modelosHtml = await response.text();
        },

        setUnidadeSelectValue(unidadeId) {
            const el = document.getElementById("unidade_id");
            if (!el) return;
            const value = unidadeId != null ? String(unidadeId) : "";
            if (value && Array.from(el.options).some((opt) => opt.value === value)) {
                el.value = value;
            }
        },

        getSelectValue(id) {
            const el = document.getElementById(id);
            if (!el) {
                return null;
            }
            return el.value;
        },

        getSelectValues(id) {
            const el = document.getElementById(id);
            if (!el) {
                return [];
            }
            return Array.from(el.selectedOptions || []).map((opt) => opt.value);
        },

        // --- Confirmar vínculo e fechar modal ---
        async confirmarModal() {
            this.modal.error = null;
            const detWrapper = this.dets.find(
                (d) => d.data.key === this.modal.key
            );
            if (!detWrapper) {
                return;
            }
            const det = detWrapper.data;

            if (
                this.modal.tipo === "existente" &&
                !this.modal.produtoSelecionado
            ) {
                this.modal.error = "Selecione um produto existente";
                return;
            }
            if (this.modal.tipo === "novo" && !this.modal.produtoName) {
                this.modal.error = "Informe o nome do produto";
                return;
            }

            const unidadeId = this.getSelectValue("unidade_id");
            if (!unidadeId || unidadeId === "0") {
                this.modal.error = "Selecione a unidade do produto";
                return;
            }
            const unidadeLabel = (() => {
                const el = document.getElementById("unidade_id");
                if (!el) {
                    return det.uCom;
                }
                const option = el.options[el.selectedIndex];
                return option ? option.text : det.uCom;
            })();
            const marcaId = this.getSelectValue("marca_id");
            const modeloId = this.getSelectValue("modelo_id");
            const categorias = this.getSelectValues("categorias");

            // Catálogo (NCM/CEST/cEAN) sempre. Seed fiscal só em produto novo.
            // Produto existente: backend preserva tributação de saída (não depender só do front).
            const produtoPayload = {
                unidade_id: unidadeId,
                estoque_minimo: this.modal.estoque_minimo,
                active: this.modal.active ? 1 : 0,
                observacao: this.modal.observacao,
                NCM: det.NCM,
                CEST: det.CEST,
                cEAN: det.cEAN,
            };

            if (this.modal.tipo === "novo") {
                produtoPayload.name = this.modal.produtoName;
                produtoPayload.CFOP = det.CFOP;
                produtoPayload.icmsOrigem = det.icmsOrigem;
                produtoPayload.icmsCst = det.icmsCst;
                produtoPayload.modBC = det.modBC;
                produtoPayload.pRedBC = det.pRedBC;
                produtoPayload.pICMS = det.pICMS;
                produtoPayload.pisCst = det.pisCst;
                produtoPayload.cofinsCst = det.cofinsCst;
            } else {
                produtoPayload.id = this.modal.produtoSelecionado.id;
            }

            if (marcaId) {
                produtoPayload.marca_id = marcaId;
            }
            if (modeloId) {
                produtoPayload.modelo_id = modeloId;
            }
            if (categorias.length) {
                produtoPayload._categorias = categorias;
            }

            const baseCodes = (this.modal.codigos || []).map((c) => ({
                id: this.modal.tipo === "novo" ? c.id || null : c.id,
                code: c.code,
                empresa_id: c.empresa_id || null,
                isOriginal: c.isOriginal ?? null,
            }));
            if (baseCodes.length) {
                produtoPayload._codes = baseCodes;
            }

            const response = await fetch("/ajax/vincule-produto", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": this.getCsrfToken(),
                },
                body: JSON.stringify({
                    produto: produtoPayload,
                    detId: det.id,
                    key: det.key,
                }),
            });

            if (!response.ok) {
                this.modal.error = "Falha ao vincular item";
                return;
            }

            const produto = await response.json();
            det.produto = produto;
            this.modal.error = null;
            det.integrado = true;
            det.oMargem = 2;
            det.vCustoAnterior = this.toNumber(
                produto.preco_custo || det.vCustoAnterior
            ).toFixed(3);
            det.vMargemAnterior = this.toNumber(
                produto.margem || det.vMargemAnterior
            ).toFixed(3);
            det.uComConvertida = true;
            det.qCom = this.toNumber(this.modal.qCom);
            det.vUnCom = this.toNumber(this.modal.vUnCom);
            det.vProd = this.toNumber(this.modal.vProd);
            det.uCom = unidadeLabel;

            this.recalculaDets();
            this.hideModal();
        },

        hideModal() {
            const el = document.getElementById("panelModal");
            if (!el) {
                return;
            }
            const a = document.activeElement;
            if (
                a &&
                el.contains(a) &&
                typeof a.blur === "function"
            ) {
                a.blur();
            }
            if (window.bootstrap && window.bootstrap.Modal) {
                const inst = window.bootstrap.Modal.getInstance(el);
                if (inst) {
                    inst.hide();
                }
                return;
            }
            if (window.$ && window.$.fn && window.$.fn.modal) {
                window.$(el).modal("hide");
            }
        },

        // --- Integração NF-e (payload e envio) ---
        buildDetPayload() {
            return this.dets.map((detWrapper) => {
                const encode = JSON.stringify({
                    json: JSON.stringify(detWrapper.data),
                    original: JSON.stringify(
                        detWrapper.original || detWrapper.data
                    ),
                });
                return { json: encode };
            });
        },

        scrollIntegracaoErroIntoView() {
            this.$nextTick(() => {
                const el = document.getElementById("nfe-integracao-erro");
                if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
            });
        },

        async integrar() {
            this.error = null;
            this.success = null;

            if (this.integrarOptions.estoque) {
                const pendente = this.dets.find((det) => !det.data.integrado);
                if (pendente) {
                    this.error = "Item não integrado!";
                    this.scrollIntegracaoErroIntoView();
                    return;
                }

                const semUnidade = this.dets.find(
                    (d) =>
                        d.data.produto &&
                        !this.produtoTemUnidadeCadastro(d.data.produto)
                );
                if (semUnidade) {
                    const p = semUnidade.data.produto;
                    const nomeItem =
                        (p && p.name) || semUnidade.data.xProd || "Item";
                    this.error =
                        "Há itens com produto sem unidade definida no cadastro (campo unidade do produto). " +
                        `Verifique: ${nomeItem}. Use o assistente (⋮) no item e confirme a unidade, ou cadastre a unidade no produto.`;
                    this.scrollIntegracaoErroIntoView();
                    return;
                }

                const invalido = this.dets.find((det) => {
                    const custoReal = this.toNumber(
                        this.getVCustoValue(det.data)
                    );
                    return this.toNumber(det.data.vCustoVendas) < custoReal;
                });
                if (invalido) {
                    this.error =
                        "Custo vendas não pode ser menor que o custo real (anterior, atual ou médio, conforme a opção escolhida).";
                    this.scrollIntegracaoErroIntoView();
                    return;
                }
            }

            if (!this.data_movimento) {
                this.error = "Informe a data de movimentação";
                this.scrollIntegracaoErroIntoView();
                return;
            }

            if (this.freteTipo === "1" && this.toNumber(this.vFrete) === 0) {
                this.error =
                    "O valor do frete FOB não pode ser zero. Importe o CTe ou informe o valor manualmente.";
                this.scrollIntegracaoErroIntoView();
                return;
            }

            this.integrando = true;

            try {
                const response = await fetch(
                    `/compras/nfe/${this.id}/integration/integrar`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                            "X-CSRF-TOKEN": this.getCsrfToken(),
                        },
                        body: JSON.stringify({
                            data_movimento: this.data_movimento,
                            integrar_financeiro: this.integrarOptions.financeiro
                                ? 1
                                : 0,
                            integrar_estoque: this.integrarOptions.estoque
                                ? 1
                                : 0,
                            det: this.integrarOptions.estoque
                                ? this.buildDetPayload()
                                : [],
                            acquisition_purposes: this.acquisitionPurposeEnabled
                                ? this.buildAcquisitionPurposePayload()
                                : undefined,
                        }),
                    }
                );

                const payload = await response.json();
                if (!response.ok || payload.error) {
                    throw new Error(
                        payload.message || "Falha ao integrar NF-e"
                    );
                }

                this.success = payload.message || "Integração concluída";
                window.location = "/compras/nfe";
            } catch (err) {
                console.error(err);
                this.error = err.message || "Falha ao integrar";
                this.scrollIntegracaoErroIntoView();
            } finally {
                this.integrando = false;
            }
        },

        getCsrfToken() {
            const token = document.head.querySelector(
                'meta[name="csrf-token"]'
            );
            return token ? token.content : "";
        },

        buildAcquisitionPurposePayload() {
            return (this.dets || []).map((wrapper) => {
                const data = wrapper.data || {};
                const purpose = data.acquisition_purpose || null;
                return {
                    det_id: data.id,
                    purpose,
                    note:
                        purpose === "OUTRA"
                            ? data.acquisition_purpose_note || null
                            : null,
                };
            });
        },

        onItemAcquisitionPurposeChange(detWrapper) {
            if (!detWrapper || !detWrapper.data) {
                return;
            }
            if (detWrapper.data.acquisition_purpose !== "OUTRA") {
                detWrapper.data.acquisition_purpose_note = "";
            }
        },
    },
};
